import {
  CanonicalCompanyComplianceContractError,
  assertCanonicalCompanyComplianceLimit,
  assertCanonicalCompanyComplianceUuid,
  parseCanonicalCompanyComplianceDetail,
  parseCanonicalCompanyComplianceDecisionResult,
  parseCanonicalCompanyComplianceHistory,
  parseCanonicalCompanyCompliancePreview,
  parseCanonicalCompanyComplianceQueue,
  validateCanonicalCompanyComplianceDecisionInput,
  type CanonicalCompanyComplianceDecision,
  type CanonicalCompanyComplianceDecisionResult,
  type CanonicalCompanyComplianceDetail,
  type CanonicalCompanyComplianceHistory,
  type CanonicalCompanyCompliancePreview,
  type CanonicalCompanyComplianceQueue,
} from "@/data/contractor/compliance/canonicalCompanyComplianceReview";
import { isCanonicalInternalComplianceReviewEnabled } from "./individual-compliance-ops-review.service";
import {
  CanonicalInternalReviewReadClientError,
  assertCanonicalInternalReviewInputKeys,
  canonicalInternalReviewFeatureDisabled,
  defaultCanonicalInternalReviewInvoker,
  parseCanonicalInternalReviewResult,
  type CanonicalInternalReviewFunctionInvoker,
} from "./canonical-internal-review-read.shared";

export const COMPANY_COMPLIANCE_OPS_REVIEW_READ_FUNCTION =
  "company-compliance-ops-review-read";
export const COMPANY_COMPLIANCE_OPS_REVIEW_DECIDE_FUNCTION =
  "company-compliance-ops-review-decide";
export const COMPANY_COMPLIANCE_OPS_REVIEW_PREVIEW_FUNCTION =
  "company-compliance-ops-review-read-url";

export type ListCompanyComplianceReviewQueueInput = Readonly<{
  organizationId: string;
  contractorId: string;
  limit?: number;
}>;

export type GetCompanyComplianceReviewDetailInput = Readonly<{
  organizationId: string;
  contractorId: string;
  recordId: string;
}>;

export type GetCompanyComplianceReviewHistoryInput = Readonly<{
  organizationId: string;
  contractorId: string;
  evidenceId: string;
}>;

export type SubmitCompanyComplianceDecisionInput = Readonly<{
  organizationId: string;
  contractorId: string;
  evidenceId: string;
  decision: CanonicalCompanyComplianceDecision;
  reasonCode?: string;
  note?: string;
  idempotencyKey: string;
  expectedLatestDecisionId?: string | null;
}>;

export type GetCompanyCompliancePreviewInput = Readonly<{
  organizationId: string;
  contractorId: string;
  evidenceId: string;
}>;

export function createCompanyComplianceOpsReviewReadClient(dependencies?: {
  invoke?: CanonicalInternalReviewFunctionInvoker;
  enabled?: () => boolean;
}) {
  const invoke = dependencies?.invoke ?? defaultCanonicalInternalReviewInvoker;
  const enabled =
    dependencies?.enabled ?? isCanonicalInternalComplianceReviewEnabled;

  function assertEnabled(): void {
    if (!enabled()) {
      canonicalInternalReviewFeatureDisabled(
        "company_compliance_ops_review_feature_disabled",
      );
    }
  }

  async function invokeRead<T>(
    body: Readonly<Record<string, unknown>>,
    parser: (value: unknown) => T,
  ): Promise<T> {
    try {
      return parseCanonicalInternalReviewResult(
        await invoke(COMPANY_COMPLIANCE_OPS_REVIEW_READ_FUNCTION, { body }),
        parser,
        "company_compliance_ops_review_",
      );
    } catch (error) {
      if (error instanceof CanonicalInternalReviewReadClientError) throw error;
      if (error instanceof CanonicalCompanyComplianceContractError) {
        throw new CanonicalInternalReviewReadClientError(
          "unexpected_error",
          error.code,
          null,
        );
      }
      throw error;
    }
  }

  async function invokeContract<T>(
    functionName: string,
    body: Readonly<Record<string, unknown>>,
    parser: (value: unknown) => T,
  ): Promise<T> {
    return parseCanonicalInternalReviewResult(
      await invoke(functionName, { body }),
      parser,
      "company_compliance_ops_review_",
    );
  }

  return Object.freeze({
    async listQueue(
      input: ListCompanyComplianceReviewQueueInput,
    ): Promise<CanonicalCompanyComplianceQueue> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(
        input,
        ["organizationId", "contractorId", "limit"],
        "company_compliance_ops_review_request_invalid",
      );
      const body = Object.freeze({
        operation: "list_queue",
        organizationId:
          assertCanonicalCompanyComplianceUuid(input.organizationId),
        contractorId:
          assertCanonicalCompanyComplianceUuid(input.contractorId),
        ...(input.limit === undefined
          ? {}
          : { limit: assertCanonicalCompanyComplianceLimit(input.limit) }),
      });
      return invokeRead(body, parseCanonicalCompanyComplianceQueue);
    },

    async getDetail(
      input: GetCompanyComplianceReviewDetailInput,
    ): Promise<CanonicalCompanyComplianceDetail> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(
        input,
        ["organizationId", "contractorId", "recordId"],
        "company_compliance_ops_review_request_invalid",
      );
      const body = Object.freeze({
        operation: "get_detail",
        organizationId:
          assertCanonicalCompanyComplianceUuid(input.organizationId),
        contractorId:
          assertCanonicalCompanyComplianceUuid(input.contractorId),
        recordId: assertCanonicalCompanyComplianceUuid(input.recordId),
      });
      return invokeRead(body, parseCanonicalCompanyComplianceDetail);
    },

    async getHistory(
      input: GetCompanyComplianceReviewHistoryInput,
    ): Promise<CanonicalCompanyComplianceHistory> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(
        input,
        ["organizationId", "contractorId", "evidenceId"],
        "company_compliance_ops_review_request_invalid",
      );
      const body = Object.freeze({
        operation: "get_history",
        organizationId:
          assertCanonicalCompanyComplianceUuid(input.organizationId),
        contractorId:
          assertCanonicalCompanyComplianceUuid(input.contractorId),
        evidenceId: assertCanonicalCompanyComplianceUuid(input.evidenceId),
      });
      return invokeRead(body, parseCanonicalCompanyComplianceHistory);
    },

    async submitDecision(
      input: SubmitCompanyComplianceDecisionInput,
    ): Promise<CanonicalCompanyComplianceDecisionResult> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(input, [
        "organizationId", "contractorId", "evidenceId", "decision",
        "reasonCode", "note", "idempotencyKey", "expectedLatestDecisionId",
      ], "company_compliance_ops_review_request_invalid");
      const normalized = validateCanonicalCompanyComplianceDecisionInput({
        decision: input.decision,
        reasonCode: input.reasonCode,
        note: input.note,
        idempotencyKey: input.idempotencyKey,
      });
      const body = Object.freeze({
        operation: "append_decision",
        organizationId: assertCanonicalCompanyComplianceUuid(input.organizationId),
        contractorId: assertCanonicalCompanyComplianceUuid(input.contractorId),
        evidenceId: assertCanonicalCompanyComplianceUuid(input.evidenceId),
        decision: normalized.decision,
        ...(normalized.reasonCode ? { reasonCode: normalized.reasonCode } : {}),
        ...(normalized.note ? { note: normalized.note } : {}),
        idempotencyKey: normalized.idempotencyKey,
        expectedLatestDecisionId:
          input.expectedLatestDecisionId == null
            ? null
            : assertCanonicalCompanyComplianceUuid(
              input.expectedLatestDecisionId,
            ),
      });
      return invokeContract(
        COMPANY_COMPLIANCE_OPS_REVIEW_DECIDE_FUNCTION,
        body,
        parseCanonicalCompanyComplianceDecisionResult,
      );
    },

    async getPreview(
      input: GetCompanyCompliancePreviewInput,
    ): Promise<CanonicalCompanyCompliancePreview> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(
        input,
        ["organizationId", "contractorId", "evidenceId"],
        "company_compliance_ops_review_request_invalid",
      );
      return invokeContract(
        COMPANY_COMPLIANCE_OPS_REVIEW_PREVIEW_FUNCTION,
        Object.freeze({
          operation: "create_signed_preview",
          organizationId:
            assertCanonicalCompanyComplianceUuid(input.organizationId),
          contractorId:
            assertCanonicalCompanyComplianceUuid(input.contractorId),
          evidenceId: assertCanonicalCompanyComplianceUuid(input.evidenceId),
        }),
        parseCanonicalCompanyCompliancePreview,
      );
    },
  });
}
